import {
  DianaDataType,
  EntityDataType,
  IFilter,
  IFiltersValues,
  IFlow,
  IProperty,
  IWork,
  PageViewModeEnum,
  prepareWorksForWorkGrid,
  SortModel,
  useIsOnline,
  urlWithHost,
  useDisableLastVisitedWork,
  useEnvironment,
  useHeaderHeightForScrollableContent,
  useServerAggregatedColumnOperation,
  WorkGrid,
} from '@diana/core';
import { Box } from '@mui/material';
import { GridApi, GridSortModel } from '@mui/x-data-grid-premium';
import { forwardRef, useCallback, useMemo } from 'react';
import { routeUrls } from '../../app/Routing/RouteUrls';
import { useWorkboardQuery } from './useWorkboardQuery';
import { getOfflineFilteredWorks } from './Support';

export type WorkTableProps = {
  flow: IFlow;
  storageKey: string;
  forceEmptySelection: boolean;
  supportedProperties: IProperty[];
  viewMode: PageViewModeEnum;
  appliedFilters: string[];
  showSidePeek?: boolean;
  availableFilters: IFilter[];
  filtersValues: IFiltersValues;
  onSortModelChange: (model: GridSortModel) => void;
  sortModel: SortModel;
};

export const WorkTable = forwardRef<GridApi, WorkTableProps>((props, ref) => {
  const {
    flow,
    storageKey,
    forceEmptySelection,
    supportedProperties,
    viewMode,
    showSidePeek,
    appliedFilters,
    availableFilters,
    filtersValues,
    sortModel,
    onSortModelChange,
  } = props;

  const {
    data: works,
    isFetching,
    fetchNextPage,
  } = useWorkboardQuery({
    flow,
    viewMode,
    sortModel,
  });
  const isOnline = useIsOnline();
  const isOfflineModeEnabled = flow.featureFlags?.isOfflineModeEnabled === true;

  const worksToDisplay = useMemo(() => {
    return getOfflineFilteredWorks({ works, isOfflineModeEnabled, isOnline });
  }, [works, isOfflineModeEnabled, isOnline]);

  const preparedWorks = prepareWorksForWorkGrid({
    works: worksToDisplay,
    properties: supportedProperties,
  });

  const isExpandedFeatureEnabled =
    viewMode === PageViewModeEnum.TableExpanded
      ? flow.featureFlags?.isExpandedFeatureEnabled
      : false;

  const aggregateColumnOperation = useServerAggregatedColumnOperation({
    filters: availableFilters,
    filtersValues,
    properties: supportedProperties,
    dianaDataType: DianaDataType.Work,
    flows: [flow],
    showCompleted: true,
    showDeleted: false,
    showInProgress: true,
    isExpandedFeatureEnabled,
  });

  const disableLastVisited = useDisableLastVisitedWork(flow);
  const columns = supportedProperties;
  const headerHeight = useHeaderHeightForScrollableContent();
  const height = `calc(100vh - ${headerHeight}px)`;
  const environment = useEnvironment();
  const createWorkLink = useCallback(
    (work: IWork) => urlWithHost(routeUrls.work.url({ workId: work.id, environment }, {})),
    [environment],
  );

  return (
    <Box sx={{ height, width: '100%' }}>
      <WorkGrid
        ref={ref}
        isManageColumnsPinned
        disableConfigSave={false}
        showSidePeek={showSidePeek}
        isOfflineModeEnabled={isOfflineModeEnabled}
        rows={preparedWorks}
        dataType={EntityDataType.Work}
        forceEmptySelection={forceEmptySelection}
        columns={columns.map((e) => ({ ...e, sortable: !!e.valuePath }))}
        storageKey={storageKey}
        createWorkLink={createWorkLink}
        disableLastVisited={disableLastVisited}
        filters={appliedFilters}
        aggregateColumnOperation={aggregateColumnOperation}
        gridProps={{
          height,
          autoHeight: false,
          sortingMode: 'server',
          onSortModelChange,
          loading: isFetching,
          ...(flow.featureFlags?.isExpandedFeatureEnabled &&
            viewMode === PageViewModeEnum.TableExpanded && { idKey: 'rowId' }),
          onRowsScrollEnd: () => {
            fetchNextPage();
          },
        }}
      />
    </Box>
  );
});
